import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchPreparationOperations,
  createPreparationOperation,
  updatePreparationOperation,
  deletePreparationOperation,
} from '../../store/slices/preparationOperationSlice';
import { PreparationOperation } from '../../types';

const btn: React.CSSProperties = {
  border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 14px', cursor: 'pointer',
  fontWeight: 600, fontSize: '13px', color: 'var(--crm-info)', background: 'var(--crm-info-bg)',
};

const inp: React.CSSProperties = {
  border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 10px',
  fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)', width: '100%', boxSizing: 'border-box',
};

const successButton: React.CSSProperties = {
  ...btn, color: 'var(--crm-success)', background: 'var(--crm-success-bg)', borderColor: 'var(--crm-success)',
};
const dangerButton: React.CSSProperties = {
  ...btn, color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', borderColor: 'var(--crm-danger)',
};
const neutralButton: React.CSSProperties = {
  ...btn, color: 'var(--crm-text)', background: 'var(--crm-surface-alt)', borderColor: 'var(--crm-border)',
};

const DEFAULT_COLORS = [
  '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
  '#3498db', '#9b59b6', '#607d8b', '#795548', '#16a085',
];

interface FormState {
  name: string;
  description: string;
  color: string;
  sortOrder: string;
}

const emptyForm = (): FormState => ({ name: '', description: '', color: '#607d8b', sortOrder: '0' });

const PreparationOperationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((s) => s.preparationOperation);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPreparationOperations());
  }, [dispatch]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (op: PreparationOperation) => {
    setEditingId(op.id);
    setForm({ name: op.name, description: op.description, color: op.color, sortOrder: String(op.sortOrder) });
    setError(null);
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setError(null); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Назва обовʼязкова'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        color: form.color,
        sortOrder: parseInt(form.sortOrder) || 0,
      };
      if (editingId) {
        await dispatch(updatePreparationOperation({ id: editingId, data: payload })).unwrap();
      } else {
        await dispatch(createPreparationOperation(payload)).unwrap();
      }
      closeModal();
    } catch {
      setError('Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Видалити операцію "${name}"? Вона буде прибрана з усіх інгредієнтів.`)) return;
    try {
      await dispatch(deletePreparationOperation(id)).unwrap();
    } catch {
      alert('Помилка видалення');
    }
  };

  return (
    <div className="page-shell" style={{ maxWidth: '800px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>
        Операції підготовки
      </h2>
      <p style={{ color: 'var(--crm-text-muted)', fontSize: '13px', marginBottom: '16px' }}>
        Список операцій (наприклад: «Нарізка», «Варіння», «Маринування»). Операції призначаються на інгредієнти страв та н/ф і використовуються для групованого друку в бланках підготовки.
      </p>

      <button onClick={openNew} style={{ ...successButton, marginBottom: '16px' }}>
        + Додати операцію
      </button>

      {loading && <p style={{ color: 'var(--crm-text-muted)' }}>Завантаження...</p>}

      <div style={{ display: 'grid', gap: '10px' }}>
        {items.map((op) => (
          <div
            key={op.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              background: 'var(--crm-surface)', border: '1px solid var(--crm-border)',
              borderRadius: '8px', padding: '12px 16px',
              borderLeft: `4px solid ${op.color}`,
            }}
          >
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: op.color, flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--crm-text)' }}>{op.name}</div>
              {op.description && (
                <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginTop: '2px' }}>{op.description}</div>
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--crm-text-muted)', marginRight: '8px' }}>
              Порядок: {op.sortOrder}
            </div>
            <button onClick={() => openEdit(op)} style={{ ...btn, padding: '5px 12px' }}>✏</button>
            <button onClick={() => handleDelete(op.id, op.name)} style={{ ...dangerButton, padding: '5px 12px' }}>×</button>
          </div>
        ))}
        {!loading && items.length === 0 && (
          <div style={{ color: 'var(--crm-text-muted)', textAlign: 'center', padding: '32px' }}>
            Операцій поки немає. Натисніть «+ Додати операцію».
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="preparation-operations-modal-overlay" style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="preparation-operations-modal" style={{
            background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-lg)', padding: '24px',
            width: '480px', maxWidth: '95vw', boxShadow: 'var(--crm-shadow-sm)',
          }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--crm-text)' }}>
              {editingId ? 'Редагувати операцію' : 'Нова операція'}
            </h3>

            <label style={{ fontSize: '12px', color: 'var(--crm-text-muted)', display: 'block', marginBottom: '4px' }}>
              Назва *
            </label>
            <input
              style={{ ...inp, marginBottom: '12px' }}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Нарізка, Варіння, Маринування..."
              autoFocus
            />

            <label style={{ fontSize: '12px', color: 'var(--crm-text-muted)', display: 'block', marginBottom: '4px' }}>
              Опис
            </label>
            <input
              style={{ ...inp, marginBottom: '12px' }}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Необов'язково..."
            />

            <label style={{ fontSize: '12px', color: 'var(--crm-text-muted)', display: 'block', marginBottom: '6px' }}>
              Колір
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', alignItems: 'center' }}>
              {DEFAULT_COLORS.map((c) => (
                <div
                  key={c}
                  onClick={() => setForm((p) => ({ ...p, color: c }))}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%', background: c, cursor: 'pointer',
                    border: form.color === c ? '3px solid var(--crm-text)' : '2px solid transparent',
                    boxSizing: 'border-box',
                  }}
                />
              ))}
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                style={{ width: '36px', height: '28px', border: 'none', cursor: 'pointer', background: 'none' }}
                title="Власний колір"
              />
            </div>

            <label style={{ fontSize: '12px', color: 'var(--crm-text-muted)', display: 'block', marginBottom: '4px' }}>
              Порядок сортування
            </label>
            <input
              type="number"
              style={{ ...inp, marginBottom: '16px' }}
              value={form.sortOrder}
              onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
              min="0"
            />

            {error && (
              <div role="alert" style={{ color: 'var(--crm-danger)', background: 'var(--crm-danger-bg)', padding: 8, borderRadius: 'var(--crm-radius-sm)', fontSize: '13px', marginBottom: '12px' }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={neutralButton}>Скасувати</button>
              <button onClick={handleSave} disabled={saving} style={successButton}>
                {saving ? 'Збереження...' : 'Зберегти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreparationOperationsPage;
